import { useId } from 'react';
import { useField } from 'formik';
import ErrorLabel from './ErrorLabel';
import { FormRowWrapper, FormInputWithLabel } from './Styled';
import { shouldDisplayError, shouldHideInput, BaseFieldProps } from './utils';

import BasePhoneInput, {
  PhoneInputProps as BaseProps
} from 'components/PhoneInput';

interface PhoneInputProps extends BaseFieldProps<{}> {
  /** Defines a string value that labels the current element. */
  'aria-label'?: string;
  /**Defines the value assign to the current element */
  value?: any;
  /**Event: is trigger with every change in the current element */
  onChange?: any;
  /**Defines a string to specify a short hint for the current element  */
  placeholder?: string;
  /** Ref to the underlying phone number `<input>` element. */
  inputRef?: BaseProps['inputRef'];
  /** Shows the number without letting it, or its country, be changed. */
  readOnly?: boolean;
}

function Input({
  name,
  value,
  validate,
  className,
  inputRef,
  ...props
}: PhoneInputProps) {
  const fieldHookProps = {
    name,
    value,
    validate
  };

  const [field, meta, helpers] = useField(fieldHookProps);
  const isError = shouldDisplayError(meta);
  const isHidden = shouldHideInput(props);
  const fieldId = useId();
  // Semantic UI's `FormField` derives `aria-describedby`/`aria-invalid` from
  // its own `id` prop and forwards them to `control` (`BasePhoneInput`)
  // *after* `numberInputProps`, so they'd otherwise clobber whatever we set
  // there. Giving it an `id` lets it compute the same id we use below.
  const errorId = `${fieldId}-error-message`;

  const handleChange: BaseProps['onChange'] = (value: any) => {
    helpers.setValue(value || '');
  };

  const handleBlur: BaseProps['onBlur'] = () => {
    helpers.setTouched(true);
  };

  return (
    <FormRowWrapper className={className} hidden={isHidden}>
      <FormInputWithLabel
        {...field}
        {...props}
        id={fieldId}
        inputRef={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        error={isError}
        numberInputProps={{
          name,
          'aria-label': props['aria-label'] || props.label
        }}
      />
      <ErrorLabel
        id={errorId}
        active={isError}
        content={meta.error}
        textAlign={'left'}
      />
    </FormRowWrapper>
  );
}

Input.defaultProps = {
  control: BasePhoneInput,

  /** Sets the default country. */
  defaultCountry: 'US',

  /** Set the code not editable */
  countryCallingCodeEditable: false,

  /** Set format to international */
  international: true
};

export default Input;
